import React from 'react';
import { 
  Leaf, ShieldCheck, Zap, 
  CheckCircle, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { servicesData } from '../data/servicesData';
import { motion } from 'framer-motion';

// --- Components ---

const SectionHeading = ({ sub, title, light = false }: { sub: string, title: React.ReactNode, light?: boolean }) => (
  <div className="text-center max-w-3xl mx-auto mb-16">
    <span className={`font-bold tracking-widest uppercase text-sm mb-2 block ${light ? 'text-simba-gold' : 'text-simba-green'}`}>
      {sub}
    </span>
    <h2 className={`font-display text-4xl lg:text-5xl ${light ? 'text-white' : 'text-simba-dark'}`}>
      {title}
    </h2>
    <div className={`h-1.5 w-24 bg-simba-gold rounded-full mx-auto mt-6 ${light ? '' : ''}`}></div>
  </div>
);

// 1. About / Story / Mission Section
export const AboutSection = () => {
  return (
    <section className="py-24 bg-simba-cream relative overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-simba-green/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-simba-gold/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
            <SectionHeading sub="About Us" title={<>The Story Behind Our <span className="italic text-simba-green">Passion</span> for Healing</>} />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div className="space-y-8">
                    <p className="text-xl leading-relaxed text-gray-700 font-medium">
                        Simba Herbals was founded on a deep belief in nature’s ability to heal. We craft potent, authentic herbal remedies using time-honored traditions and modern science to support your well-being naturally.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                          className="p-6 bg-white rounded-2xl shadow-sm border border-simba-green/10 hover:shadow-md transition-shadow"
                        >
                             <div className="w-10 h-10 bg-simba-green/10 rounded-full flex items-center justify-center mb-4">
                                <Leaf className="text-simba-green" size={20} />
                             </div>
                             <h4 className="font-display font-bold text-lg text-simba-dark mb-2">Our Vision</h4>
                             <p className="text-sm text-gray-600">Empowering lives with nature's healing power through authentic solutions.</p>
                        </motion.div>
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                          className="p-6 bg-white rounded-2xl shadow-sm border border-simba-green/10 hover:shadow-md transition-shadow"
                        >
                             <div className="w-10 h-10 bg-simba-green/10 rounded-full flex items-center justify-center mb-4">
                                <ShieldCheck className="text-simba-green" size={20} />
                             </div>
                             <h4 className="font-display font-bold text-lg text-simba-dark mb-2">Our Mission</h4>
                             <p className="text-sm text-gray-600">To provide potent, science-backed herbal remedies for optimal health.</p>
                        </motion.div>
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: 0.3 }}
                          className="p-6 bg-white rounded-2xl shadow-sm border border-simba-green/10 hover:shadow-md transition-shadow"
                        >
                             <div className="w-10 h-10 bg-simba-green/10 rounded-full flex items-center justify-center mb-4">
                                <Zap className="text-simba-green" size={20} />
                             </div>
                             <h4 className="font-display font-bold text-lg text-simba-dark mb-2">Our Motto</h4>
                             <p className="text-sm text-gray-600">Harness nature's strength, embrace vitality, and live your best life.</p>
                        </motion.div>
                    </div>
                </div>
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="relative"
                >
                    <div className="absolute inset-0 bg-simba-green rounded-[3rem] rotate-3 opacity-20 transform scale-105"></div>
                    <img 
                        src="/images/section/chyavanprash-or-chyawanprash-is-widely-consumed-in-india-as-a-dietary-ayurvedic-supplement.jpg" 
                        alt="Herbal Preparation" 
                        className="relative rounded-[3rem] shadow-2xl w-full h-[500px] object-cover"
                    />
                </motion.div>
            </div>
        </div>
    </section>
  );
};

// 2. Stats Section
export const StatsSection = () => {
    return (
        <section className="py-20 bg-simba-dark text-white">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/10">
                    <div className="p-4">
                        <div className="text-5xl font-display font-bold text-simba-gold mb-2">20+</div>
                        <div className="text-sm uppercase tracking-widest text-gray-400">Holistic Products</div>
                    </div>
                    <div className="p-4">
                        <div className="text-5xl font-display font-bold text-simba-gold mb-2">1k+</div>
                        <div className="text-sm uppercase tracking-widest text-gray-400">Happy Clients</div>
                    </div>
                     <div className="p-4">
                        <div className="text-5xl font-display font-bold text-simba-gold mb-2">10+</div>
                        <div className="text-sm uppercase tracking-widest text-gray-400">Years Experience</div>
                    </div>
                    <div className="p-4">
                        <div className="text-5xl font-display font-bold text-simba-gold mb-2">4.7</div>
                        <div className="text-sm uppercase tracking-widest text-gray-400">Average Rating</div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// 3. Services Section


// ... (previous components)

// 3. Services Section
export const ServicesSection = () => {
    return (
        <section className="py-24 bg-white">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                <SectionHeading sub="Our Services" title="Experience Bold, Natural Remedies" />
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {servicesData.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Link to={`/services/${s.id}`} key={i} className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer h-96 block">
                                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-simba-dark/90 via-simba-dark/40 to-transparent"></div>
                                
                                <div className="absolute bottom-0 left-0 p-8 w-full group-hover:-translate-y-2 transition-transform duration-300">
                                    <div className="text-simba-gold mb-4 mb-2">
                                        <Icon size={32} />
                                    </div>
                                    <h3 className="text-2xl font-display font-bold text-white mb-2">{s.title}</h3>
                                    <p className="text-gray-200 text-sm mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0 text-sm font-medium">{s.shortDesc}</p>
                                    <span className="inline-flex items-center text-simba-green font-bold text-sm uppercase tracking-wider group-hover:text-simba-gold transition-colors">
                                        Learn More <ArrowRight size={16} className="ml-2" />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

// Mufasa Protocol Section
export const MufasaProtocolSection = () => {
    return (
        <section className="py-24 bg-simba-dark text-white relative overflow-hidden">
             {/* Background Pattern */}
             <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/black-scales.png')]"></div>
             
             <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                    
                    {/* Visual Side */}
                    <div className="relative h-full">
                        <div className="absolute top-4 -left-4 w-full h-full border-2 border-simba-green rounded-[2rem]"></div>
                        <img 
                            src="/images/pics/The Mufasa Protocol.webp" 
                            alt="Lion Strength" 
                            className="relative rounded-[2rem] shadow-2xl w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-700"
                        />
                    </div>

                    {/* Text Content */}
                    <div className="space-y-12">
                         <div>
                             <span className="text-simba-gold font-bold text-sm uppercase tracking-widest mb-2 block">Stop settling for average</span>
                             <h2 className="font-display text-4xl lg:text-5xl text-white mb-6">The Mufasa Protocol: A New Era of Masculine Mastery</h2>
                             <p className="text-xl text-gray-300 leading-relaxed font-light border-l-4 border-simba-green pl-6">
                                At Simba Herbals UK, we bridge the gap between ancient African botanical wisdom and modern masculine requirements. Our mission is simple: to help you reclaim your throne with 100% African Natural Herbs.
                             </p>
                         </div>

                         <div className="space-y-6">
                             <h3 className="font-display text-2xl text-simba-gold">A Holistic Approach to Restoration</h3>
                             <p className="text-gray-400 leading-relaxed">
                                 In a world of high-stress UK lifestyles, your "King Energy" can often feel drained. Long working hours in London or Manchester, combined with poor sleep and processed diets, take a toll on masculine vigour. Furthermore, chemical "quick fixes" often come with unwanted side effects like headaches or heart palpitations.
                             </p>
                             <p className="text-gray-400 leading-relaxed">
                                 Consequently, we developed the <strong>Mufasa Protocol</strong>—a holistic approach to restorative vitality. We use rare botanical alkaloids sourced directly from the Congo rainforest. We prioritise purity, transparency, and results that speak for themselves. This isn't just about a moment; it's about a lifestyle of dominance and natural strength.
                             </p>
                         </div>

                         <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
                             <h4 className="font-bold text-simba-green uppercase tracking-wide mb-2 flex items-center">
                                <Zap className="mr-2" size={20} />
                                Explore Our Core Wellness Categories
                             </h4>
                             <p className="text-sm text-gray-400">
                                To rule your jungle effectively, you need the right tools for every occasion. We have categorised our range to suit your specific lifestyle needs.
                             </p>
                         </div>
                    </div>

                 </div>
             </div>
        </section>
    );
};



// 4. Pricing / Packages
export const PricingSection = () => {
    const packages = [
        {
            name: "Basic Package",
            price: "49",
            features: ["Performance Assessment", "Essential Detox Formula", "Limited Consultation", "Standard Enhancers"]
        },
        {
            name: "Premium Package",
            price: "99",
            bg: "bg-simba-dark text-white",
            highlight: true,
            features: ["Advanced Libido Assessment", "Full-Body Detox Program", "Unlimited Consultations", "Premium Formulations", "VIP Discounts"]
        },
         {
            name: "Pro Package",
            price: "149",
            features: ["In-Depth Evaluation", "Intense Detox Support", "Unlimited 1-on-1", "High-Potency Boosters", "Priority Access"]
        },
        {
            name: "Ultimate Package",
            price: "199",
            features: ["Full Focus Optimization", "Deep Endurance Enhancement", "Unlimited Expert Access", "Ultimate-Strength Formulas", "Lifetime VIP Status"]
        }
    ];

    return (
        <section className="py-24 bg-simba-cream">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                 <SectionHeading sub="Choose Your Package" title="Find the Right Wellness Plan" />

                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {packages.map((pkg, i) => (
                        <div key={i} className={`rounded-3xl p-8 shadow-xl relative flex flex-col ${pkg.highlight ? 'bg-simba-dark text-white ring-4 ring-simba-gold/30 transform lg:-translate-y-4' : 'bg-white text-simba-dark'}`}>
                             {pkg.highlight && (
                                 <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-simba-gold text-simba-dark font-bold px-4 py-1 rounded-full text-sm uppercase">Most Popular</div>
                             )}
                             <h3 className="font-display font-bold text-xl mb-4">{pkg.name}</h3>
                             <div className="text-4xl font-bold mb-6 font-display">£{pkg.price}</div>
                             
                             <ul className="space-y-4 mb-8 flex-1">
                                 {pkg.features.map((f, j) => (
                                     <li key={j} className="flex items-start text-sm">
                                         <CheckCircle size={16} className={`mr-2 mt-0.5 flex-shrink-0 ${pkg.highlight ? 'text-simba-green' : 'text-simba-gold'}`} />
                                         <span className={pkg.highlight ? 'text-gray-300' : 'text-gray-600'}>{f}</span>
                                     </li>
                                 ))}
                             </ul>
                             
                             <Link
                                 to={`/contact?package=${encodeURIComponent(pkg.name)}`}
                                 className={`block w-full py-4 rounded-xl font-bold transition-all text-center ${pkg.highlight ? 'bg-simba-green text-white hover:bg-simba-gold' : 'bg-simba-cream text-simba-dark hover:bg-simba-green hover:text-white'}`}
                             >
                                 Get Started
                             </Link>
                             <p className="text-xs text-center mt-4 opacity-60">*100% natural ingredients</p>
                        </div>
                    ))}
                 </div>
            </div>
        </section>
    );
};

// 5. Order Form
export const OrderSection = () => {
    return (
        <section className="py-24 bg-white">
            <div className="max-w-4xl mx-auto px-6">
                <div className="bg-simba-cream rounded-[3rem] p-8 lg:p-16 shadow-2xl border border-simba-gold/20">
                    <SectionHeading sub="Order Online" title="Start Your Journey Today" />
                    
                    <form className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="font-bold text-sm text-simba-dark uppercase">Full Name</label>
                                <input type="text" className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-simba-green focus:ring-0 outline-none transition-colors" placeholder="John Doe" />
                            </div>
                            <div className="space-y-2">
                                <label className="font-bold text-sm text-simba-dark uppercase">Phone</label>
                                <input type="tel" className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-simba-green focus:ring-0 outline-none transition-colors" placeholder="+44 7700 900000" />
                            </div>
                        </div>

                         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="font-bold text-sm text-simba-dark uppercase">Email</label>
                                <input type="email" className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-simba-green focus:ring-0 outline-none transition-colors" placeholder="john@example.com" />
                            </div>
                            <div className="space-y-2">
                                <label className="font-bold text-sm text-simba-dark uppercase">Service Interest</label>
                                <select className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-simba-green focus:ring-0 outline-none transition-colors bg-white">
                                    <option>Herbal Performance Boosters</option>
                                    <option>Detox Program</option>
                                    <option>Consultation</option>
                                    <option>Custom Formulation</option>
                                </select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="font-bold text-sm text-simba-dark uppercase">Additional Notes</label>
                            <textarea rows={4} className="w-full px-6 py-4 rounded-xl border border-gray-200 focus:border-simba-green focus:ring-0 outline-none transition-colors" placeholder="Tell us about your goals..."></textarea>
                        </div>

                        <button className="w-full bg-simba-green text-white font-bold py-5 rounded-xl text-lg hover:bg-simba-gold transition-colors shadow-lg hover:translate-y-[-2px] transform duration-200">
                            Submit Order Request
                        </button>
                        <p className="text-center text-sm text-gray-500 flex justify-center items-center">
                            <ShieldCheck size={14} className="mr-2 text-simba-green" /> 
                            Your information is kept strictly confidential.
                        </p>
                    </form>
                </div>
            </div>
        </section>
    );
};
